import {
  type ArdoqId,
  type FilterInterfaceFilter,
  type FormattingFilter,
  isTraversalLoadedState,
  type LoadedState,
  type TraversalLoadedState,
  ViewIds,
} from '@ardoq/api-types';
import {
  ExtractPayload,
  action$,
  dispatchAction,
  extractPayload,
  ofType,
  persistentReducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import {
  type ViewContext,
  type SettingsConfig,
  DEFAULT_VIEW_CONTEXT,
} from '@ardoq/view-settings';
import { DEFAULT_ARDOQ_I18NLOCALE, t, type I18nLocale } from '@ardoq/i18n';
import { getExportLabels } from 'views/getExportLabels';
import { map, debounceTime, distinctUntilKeyChanged } from 'rxjs';
import { activeView$ } from 'streams/views/mainContent/activeView$';
import {
  getLeftMenu as blockDiagramViewModifiers,
  getRightMenu as blockDiagramRightMenu,
} from 'tabview/blockDiagram/menus';
import { getLeftMenu as modernizedBlockDiagramViewModifiers } from 'tabview/blockDiagram/modernized/getLeftMenu';
import { getFlexibleBlockDiagramViewModifiers } from 'tabview/flexibleBlockDiagram/menus';
import { getAssistantBlocksViewModifiers } from 'tabview/assistantBlocks/menus';
import type { AssistantBlocksViewProperties } from 'tabview/assistantBlocks/viewModel$';
import { getSurface3DViewModifiers } from 'tabview/xyzCharts/menus';
import { getContourViewModifiers } from 'tabview/xyzCharts/contourMenus';
import { getPoints3DViewModifiers } from 'tabview/xyzCharts/points3dMenus';
import { getNetwork3DViewModifiers } from 'tabview/network3d/network3dMenus';
import { isNetwork3DViewProperties } from 'tabview/network3d/types';
import { getSpiderViewModifiers } from 'tabview/radialCharts/spider/spiderMenus';
import { isSpiderViewProperties } from 'tabview/radialCharts/spider/types';
import { getPolarViewModifiers } from 'tabview/radialCharts/polar/polarMenus';
import { isPolarViewProperties } from 'tabview/radialCharts/polar/types';
import {
  isContourViewProperties,
  isPoints3DViewProperties,
  isSurface3DViewProperties,
} from 'tabview/xyzCharts/types';
import { isFlexibleBlockDiagramViewProperties } from 'tabview/flexibleBlockDiagram/types';
import type {
  BlockDiagramViewProperties,
  ModernizedBlockDiagramViewProperties,
} from 'tabview/blockDiagram/types';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import type { ExtractStreamShape } from 'tabview/types';
import { updateViewContext, updateViewProperties } from '@ardoq/view-settings';
import { RelationshipsViewSettings } from 'tabview/relationsD3View/hierarchical/types';
import { isTimelineSettingsControlsDependencies } from '@ardoq/timeline2024';
import { getGraphNodeCount } from '@ardoq/graph';
import { debounce, isEqual } from 'lodash';
import { isSameViewProperties } from './isSameViewProperties';
import {
  getExportsForHtmlView,
  getExportsForSvgView,
  getExportsForYFilesView,
} from '@ardoq/export';
import { trackExportingVisualization } from 'tracking/events/visualizations';
import { addToPresentation } from 'viewSettings/exportHandlers';
import { getStyleSheets } from 'tabview/getSharedExportFunctions';
import { getRightMenuConfig } from 'viewSettings/getRightMenuConfig';
import { toggleLegend } from 'presentation/viewPane/actions';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { distinctUntilChanged } from 'rxjs/operators';
import { viewLegendCommands } from '@ardoq/view-legend';
import { pngExporter } from 'tabview/pngExporter';
import { svgExporter } from 'tabview/svgExporter';
import { logError } from '@ardoq/logging';
import { getSharedExportFunctions } from 'tabview/getSharedExportFunctions';
import { isBlocksViewPropertiesWithViewModifiers } from 'tabview/blocks/viewModel$/viewModifiers';
import { loadedState$ } from 'loadedState/loadedState$';
import { context$ } from 'streams/context/context$';
import { formattingFilters$ } from 'streams/filters/formattingFilters$';
import { openAddToDashboardDialog } from 'dashboard/addToDashboard/actions';
import { createBubbleChartBuildWidget } from 'tabview/bubbleChart/addToDashboard/createBubbleChartBuildWidget';
import { componentMatrix2LeftMenu } from 'tabview/componentMatrix/componentMatrix2/leftMenu2';
import { isComponentMatrix2SettingsControlsDependencies } from 'tabview/componentMatrix/componentMatrix2/settingsDependencies';
import {
  hierarchicalPackingViewpointModifiers,
  hierarchicalTreemapViewpointModifiers,
  sunburstViewpointModifiers,
} from 'tabview/hierarchicalTreemap/viewpoint/viewModifiers';
import type {
  HierarchicalTreemapViewpointViewProperties,
  HierarchicalViewpointViewProperties,
} from 'tabview/hierarchicalTreemap/viewpoint/types';
import { viewpointModeSettingsForAllViews$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { quickPerspectiveReferenceTypeFilters$ } from 'streams/filters/quickPerspectiveReferenceTypeFilters$';
import {
  isViewpointModeBubbleChartSettingsControlsDependencies,
  toViewpointModeViewModifiers,
} from 'tabview/bubbleChart/viewpointMode/settingsControls';
import {
  isViewpointModeDependencyMapSettingsControlsDependencies,
  toViewpointModeViewModifiers as toDependencyMapViewpointModeViewModifiers,
} from 'tabview/dependencyMap/viewpointMode/settingsControls';
import {
  isRelationshipsSettingsControlsDependencies,
  toViewpointModeRelationshipsViewModifiers,
} from 'tabview/relationsD3View/hierarchical/viewpointMode/settingsControls';

/**
 * Three ways of drawing the same hierarchy, each with its own controls.
 *
 * Only the treemap's take the treemap's settings; the other two have no tiling method to offer, so
 * their modifiers take the narrower properties type rather than one all three share.
 */
const HIERARCHICAL_VIEW_MODIFIERS = {
  [ViewIds.HIERARCHICAL_TREEMAP]: (
    properties: HierarchicalViewpointViewProperties
  ) =>
    hierarchicalTreemapViewpointModifiers(
      properties as HierarchicalTreemapViewpointViewProperties
    ),
  [ViewIds.HIERARCHICAL_PACKING]: hierarchicalPackingViewpointModifiers,
  [ViewIds.SUNBURST]: sunburstViewpointModifiers,
};

const SETTINGS_CONTROLS_DEPENDENCIES_ERROR =
  'View settings controls dependencies are wrong or missing';

const debouncedLoggers = new Map<ViewIds, ReturnType<typeof debounce>>();

const logSettingsControlDependenciesError = (viewId: ViewIds): void => {
  let log = debouncedLoggers.get(viewId);

  if (!log) {
    log = debounce(() => {
      logError(new Error(SETTINGS_CONTROLS_DEPENDENCIES_ERROR), null, {
        viewId,
      });
    }, 100);

    debouncedLoggers.set(viewId, log);
  }

  log();
};

// Late-bound accessor: exporter callbacks stored on the stream state are
// invoked long after the reducer that built them and must read the latest
// `viewContext` (current DOM container / graph component refs).
const getCurrentViewContext = (): ViewContext =>
  // oxlint-disable-next-line ardoq-lint/no-direct-stream-state-access
  viewModifiers$.state.viewContext;

export const asyncNoop = async () => {};

export type AddToDashboardOption = {
  onClick: () => Promise<void>;
  isDisabled: boolean;
};

export type ViewModifiersStreamShape = {
  mainViewId: ViewIds;
  viewPropertiesViewId: ViewIds;
  viewProperties: Record<string, any>;
  viewModifiers: SettingsConfig[];
  rightMenu: SettingsConfig[];
  viewContext: ViewContext;
  exportToPng: () => Promise<void>;
  /**
   * SVG export, wired by the views that can render one: the block diagram behind a
   * feature flag, and the flexible block diagram whenever it has content.
   */
  exportToSvg: () => Promise<void>;
  /** "Add to dashboard" entry point for the current view, or `null` when unsupported. */
  addToDashboard: AddToDashboardOption | null;
  i18nLocale: I18nLocale;
  quickPerspectiveReferenceTypeFilters: Set<string> | null;
  // Stream-derived inputs the bubble chart "add to dashboard" needs at build time.
  loadedStates: LoadedState[];
  workspaceIds: ArdoqId[];
  formattingFilters: (FormattingFilter | FilterInterfaceFilter)[];
};

const resetViewModifiers = (
  state: ViewModifiersStreamShape
): ViewModifiersStreamShape => {
  const { mainViewId, viewProperties, viewPropertiesViewId } = state;
  // Only specific views opt into SVG export, so it resets on every pass — before
  // the early return as well, which is the state a route change leaves behind
  // until the incoming view registers its properties. The closure captures one
  // view's handlers, so leaving it in place offers the row for a view that cannot
  // render one.
  if (mainViewId !== viewPropertiesViewId) {
    return {
      ...state,
      viewModifiers: [],
      exportToSvg: asyncNoop,
      addToDashboard: null,
    };
  }
  state.exportToSvg = asyncNoop;
  // only the bubble chart opts into "Add to dashboard"; reset so it never leaks across views
  state.addToDashboard = null;
  switch (mainViewId) {
    case ViewIds.BLOCK_DIAGRAM: {
      const blockDiagramProperties =
        viewProperties as unknown as BlockDiagramViewProperties;
      const {
        viewSettings,
        viewModel,
        isViewpointMode,
        noContext,
        viewInstanceId,
      } = blockDiagramProperties;
      const {
        groups,
        traversedIncomingReferenceTypes,
        traversedOutgoingReferenceTypes,
        referenceTypes,
        noConnectedComponents,
      } = viewModel;
      state.viewModifiers = blockDiagramViewModifiers({
        viewSettings,
        traversedIncomingReferenceTypes,
        traversedOutgoingReferenceTypes,
        referenceTypes,
        viewId: mainViewId,
        onViewSettingsUpdate,
        activeGroups: [...groups.add, ...groups.update],
        isViewpointMode,
        viewInstanceId,
        quickPerspectiveReferenceTypeFilters:
          state.quickPerspectiveReferenceTypeFilters,
        i18nLocale: state.i18nLocale,
      });
      const isEmptyView =
        !getGraphNodeCount(viewModel) || noConnectedComponents || noContext;

      // #region  these functions return the *current* renderingParams state, which will be initialized after this menu. */
      const getCurrentContainer = () => state.viewContext.getContainer();
      const getCurrentGraphComponent = () =>
        state.viewContext.getGraphComponent();
      // #endregion
      state.rightMenu = blockDiagramRightMenu({
        viewId: mainViewId,
        viewSettings,
        isViewpointMode: true,
        getContainer: getCurrentContainer,
        getGraphComponent: () => state.viewContext.getGraphComponent(),
        onViewSettingsUpdate,
        isEmptyView,
        i18nLocale: state.i18nLocale,
      });

      const getExports = () =>
        getExportsForYFilesView({
          container: getCurrentContainer,
          graphComponent: getCurrentGraphComponent,
          exportedViewMetadata: {
            name: mainViewId,
            isViewpointMode: true,
          },
          trackingFunction: trackExportingVisualization,
          addToPresentation,
          getStyleSheets,
          labels: getExportLabels(state.i18nLocale),
        });
      state.exportToPng = async () => await getExports().exportToPng();
      break;
    }
    case ViewIds.MODERNIZED_BLOCK_DIAGRAM: {
      const blockDiagramProperties =
        viewProperties as unknown as ModernizedBlockDiagramViewProperties;
      const { viewSettings, canvasToSvg } = blockDiagramProperties;

      state.viewModifiers = modernizedBlockDiagramViewModifiers({
        viewSettings,
        onViewSettingsUpdate,
        i18nLocale: state.i18nLocale,
      });
      const isEmptyView = !getGraphNodeCount(blockDiagramProperties);

      // #region  these functions return the *current* renderingParams state, which will be initialized after this menu. */
      const getCurrentContainer = () => state.viewContext.getContainer();
      const getCurrentGraphComponent = () =>
        state.viewContext.getGraphComponent();
      // #endregion
      state.rightMenu = blockDiagramRightMenu({
        viewId: mainViewId,
        viewSettings,
        isViewpointMode: true,
        getContainer: getCurrentContainer,
        getGraphComponent: () => state.viewContext.getGraphComponent(),
        onViewSettingsUpdate,
        isEmptyView,
        i18nLocale: state.i18nLocale,
        canvasToSvg,
      });

      const getExports = () =>
        getExportsForYFilesView({
          container: getCurrentContainer,
          graphComponent: getCurrentGraphComponent,
          exportedViewMetadata: {
            name: mainViewId,
            isViewpointMode: true,
          },
          trackingFunction: trackExportingVisualization,
          addToPresentation,
          getStyleSheets,
          labels: getExportLabels(state.i18nLocale),
          canvasToSvg,
        });
      state.exportToPng = async () => await getExports().exportToPng();
      if (canvasToSvg) {
        state.exportToSvg = async () => await getExports().exportToSvg?.();
      }
      break;
    }
    case ViewIds.DEPENDENCY_MAP_2: {
      if (
        !isViewpointModeDependencyMapSettingsControlsDependencies(
          viewProperties
        )
      ) {
        logSettingsControlDependenciesError(ViewIds.DEPENDENCY_MAP_2);
        return state;
      }
      const { viewSettings, isEmptyView } = viewProperties;
      state.viewModifiers = toDependencyMapViewpointModeViewModifiers({
        dependencies: viewProperties,
        i18nLocale: state.i18nLocale,
      });
      const exports = {
        ...getExportsForHtmlView({
          container: () => getCurrentViewContext().getContainer(),
          exportedViewMetadata: {
            name: ViewIds.DEPENDENCY_MAP_2,
            isViewpointMode: true,
          },
          trackingFunction: trackExportingVisualization,
          addToPresentation,
          showPresentationOption: true,
          getStyleSheets,
          labels: getExportLabels(state.i18nLocale),
        }),
        isDisabled: isEmptyView,
      };
      state.rightMenu = getRightMenuConfig({
        isViewpointMode: true,
        viewstate: viewSettings,
        exports,
        onViewSettingsUpdate,
        viewId: ViewIds.DEPENDENCY_MAP_2,
        i18nLocale: state.i18nLocale,
      });
      state.exportToPng = exports.exportToPng;
      break;
    }
    case ViewIds.RELATIONSHIPS_3: {
      if (!isRelationshipsSettingsControlsDependencies(viewProperties)) {
        logSettingsControlDependenciesError(ViewIds.RELATIONSHIPS_3);
        return state;
      }
      const { componentTypes, viewSettings } = viewProperties;
      state.viewModifiers = toViewpointModeRelationshipsViewModifiers({
        viewSettings,
        i18nLocale: state.i18nLocale,
      });
      const noComponents = !componentTypes.length;
      const isEmptyView = noComponents;

      const exportToPng = pngExporter({
        getExportContainer: () => getCurrentViewContext().getContainer(),
        getExportCanvas: () =>
          getCurrentViewContext().createExportCanvas?.() ?? null,
        viewId: ViewIds.RELATIONSHIPS_3,
        isViewpointMode: true,
        labels: getExportLabels(state.i18nLocale),
      });
      state.rightMenu = getRightMenuConfig({
        isViewpointMode: true,
        viewId: ViewIds.RELATIONSHIPS_3,
        viewstate: viewSettings,
        exports: {
          exportToPng,
          addToPresentation: () =>
            addToPresentation(ViewIds.RELATIONSHIPS_3, true),
          isDisabled: isEmptyView,
        },
        onViewSettingsUpdate,
        legendOnClick: () => {
          const newLegendActive = !viewSettings.isLegendActive;
          viewLegendCommands.updateViewLegendSettings({
            viewId: ViewIds.RELATIONSHIPS_3,
            isActive: newLegendActive,
          });
          dispatchAction(
            toggleLegend({
              viewId: ViewIds.RELATIONSHIPS_3,
              legendActive: newLegendActive,
            }),
            ViewIds.RELATIONSHIPS_3
          );
        },
        i18nLocale: state.i18nLocale,
      });
      state.exportToPng = exportToPng;
      break;
    }
    case ViewIds.TIMELINE: {
      if (!isTimelineSettingsControlsDependencies(viewProperties)) {
        logSettingsControlDependenciesError(ViewIds.TIMELINE);
        return state;
      }
      const { exportToPng } = getExportsForHtmlView({
        container: () => getCurrentViewContext().getContainer(),
        exportedViewMetadata: {
          name: ViewIds.TIMELINE,
          viewInstanceId: viewProperties.viewInstanceId,
          isViewpointMode: true,
        },
        trackingFunction: trackExportingVisualization,
        addToPresentation,
        showPresentationOption: true,
        getStyleSheets,
        labels: getExportLabels(state.i18nLocale),
      });

      state.viewProperties = viewProperties;
      state.exportToPng = exportToPng;
      break;
    }
    case ViewIds.BLOCKS: {
      if (!isBlocksViewPropertiesWithViewModifiers(viewProperties)) {
        logSettingsControlDependenciesError(ViewIds.BLOCKS);
        return state;
      }
      state.viewModifiers = viewProperties.viewModifiers ?? [];
      state.exportToPng = pngExporter({
        getExportContainer: () => getCurrentViewContext().getContainer(),
        getExportCanvas: () =>
          getCurrentViewContext().createExportCanvas?.() ?? null,
        viewId: ViewIds.BLOCKS,
        isViewpointMode: true,
        labels: getExportLabels(state.i18nLocale),
      });
      break;
    }
    case ViewIds.BUBBLE: {
      if (
        !isViewpointModeBubbleChartSettingsControlsDependencies(viewProperties)
      ) {
        logSettingsControlDependenciesError(ViewIds.BUBBLE);
        return state;
      }

      const getExports = () =>
        getExportsForSvgView({
          container: () => getCurrentViewContext().getContainer(),
          exportedViewMetadata: {
            name: ViewIds.BUBBLE,
            isViewpointMode: true,
          },
          ...getSharedExportFunctions(),
          labels: getExportLabels(state.i18nLocale),
        });

      state.viewProperties = viewProperties;
      state.viewModifiers = toViewpointModeViewModifiers({
        dependencies: viewProperties,
        i18nLocale: state.i18nLocale,
      });
      state.exportToPng = async () => await getExports().exportToPng();

      const {
        viewSettings: bubbleViewSettings,
        xAxisTitle,
        yAxisTitle,
        data: bubbleData,
      } = viewProperties;
      const traversalLoadedState =
        state.loadedStates.find<TraversalLoadedState>(isTraversalLoadedState);
      state.addToDashboard = {
        isDisabled: !bubbleData.length || !traversalLoadedState,
        onClick: async () => {
          if (!traversalLoadedState) {
            return;
          }
          const { workspaceIds, formattingFilters } = state;
          const displayedComponentIds = bubbleData.map(point => point.cid);
          const resolvedLabels = Object.fromEntries(
            bubbleData.map(point => [point.cid, point.label])
          );
          // Each bubble already carries the exact colour it was painted with; persist that
          // directly rather than re-resolving from the Backbone globals.
          const resolvedComponentColors = Object.fromEntries(
            bubbleData.flatMap((point): [string, string][] =>
              point.fill ? [[point.cid, point.fill]] : []
            )
          );
          const buildWidget = createBubbleChartBuildWidget({
            loadedState: traversalLoadedState,
            viewSettings: bubbleViewSettings,
            selectedComponentTypeIds: viewProperties.selectedComponentTypes,
            workspaceIds,
            conditionalFormatting: formattingFilters,
            xAxisTitle,
            yAxisTitle,
            selectedComponentIds: displayedComponentIds,
            resolvedComponentColors,
            resolvedLabels,
          });
          dispatchAction(
            openAddToDashboardDialog({
              defaultWidgetName:
                xAxisTitle && yAxisTitle
                  ? `${xAxisTitle} vs ${yAxisTitle}`
                  : t(
                      state.i18nLocale,
                      'appContainer:saveDropdown.bubbleChartWidgetDefaultName',
                      'Bubble chart'
                    ),
              buildWidget,
            })
          );
        },
      };

      break;
    }
    case ViewIds.COMPONENT_MATRIX_2: {
      if (!isComponentMatrix2SettingsControlsDependencies(viewProperties)) {
        logSettingsControlDependenciesError(ViewIds.COMPONENT_MATRIX_2);
        return state;
      }

      state.viewModifiers = componentMatrix2LeftMenu({
        dependencies: viewProperties,
        i18nLocale: state.i18nLocale,
      });
      state.rightMenu = [];

      const { exportToPng } = getExportsForHtmlView({
        container: () => getCurrentViewContext().getContainer(),
        exportedViewMetadata: {
          name: ViewIds.COMPONENT_MATRIX_2,
          isViewpointMode: true,
        },
        trackingFunction: trackExportingVisualization,
        addToPresentation,
        showPresentationOption: true,
        getStyleSheets,
        labels: getExportLabels(state.i18nLocale),
      });

      state.exportToPng = exportToPng;
      break;
    }
    case ViewIds.HIERARCHICAL_TREEMAP:
    case ViewIds.HIERARCHICAL_PACKING:
    case ViewIds.SUNBURST: {
      const hierarchicalProperties =
        viewProperties as HierarchicalViewpointViewProperties;
      state.viewModifiers = HIERARCHICAL_VIEW_MODIFIERS[mainViewId](
        hierarchicalProperties
      );
      state.rightMenu = [];

      const { exportToPng } = getExportsForHtmlView({
        container: () => getCurrentViewContext().getContainer(),
        exportedViewMetadata: {
          name: mainViewId,
          isViewpointMode: true,
        },
        trackingFunction: trackExportingVisualization,
        addToPresentation,
        showPresentationOption: true,
        getStyleSheets,
        labels: getExportLabels(state.i18nLocale),
      });

      state.exportToPng = exportToPng;
      break;
    }
    case ViewIds.ASSISTANT_DIAGRAM: {
      state.viewProperties = viewProperties;
      state.viewModifiers = [];
      state.rightMenu = [];
      state.exportToPng = asyncNoop;
      break;
    }
    case ViewIds.ASSISTANT_BLOCKS: {
      const { viewSettings } = viewProperties as AssistantBlocksViewProperties;
      state.viewProperties = viewProperties;
      state.viewModifiers = getAssistantBlocksViewModifiers({
        viewSettings,
        i18nLocale: state.i18nLocale,
      });
      state.rightMenu = [];
      state.exportToPng = asyncNoop;
      break;
    }
    case ViewIds.FLEXIBLE_BLOCK_DIAGRAM: {
      if (!isFlexibleBlockDiagramViewProperties(viewProperties)) {
        logSettingsControlDependenciesError(ViewIds.FLEXIBLE_BLOCK_DIAGRAM);
        return state;
      }
      const { viewSettings, isEmptyView, exportHandlers } = viewProperties;
      state.viewProperties = viewProperties;
      state.viewModifiers = getFlexibleBlockDiagramViewModifiers({
        viewSettings,
        i18nLocale: state.i18nLocale,
      });
      state.rightMenu = [];
      state.exportToPng = isEmptyView
        ? asyncNoop
        : pngExporter({
            getExportContainer: exportHandlers.getContainer,
            getExportCanvas: exportHandlers.createExportCanvas,
            viewId: ViewIds.FLEXIBLE_BLOCK_DIAGRAM,
            isViewpointMode: true,
            labels: getExportLabels(state.i18nLocale),
          });
      state.exportToSvg = isEmptyView
        ? asyncNoop
        : svgExporter({
            getExportContainer: exportHandlers.getContainer,
            renderSvgDocument: exportHandlers.renderSvgDocument,
            viewId: ViewIds.FLEXIBLE_BLOCK_DIAGRAM,
            isViewpointMode: true,
            labels: getExportLabels(state.i18nLocale),
          });
      break;
    }
    case ViewIds.SURFACE_3D: {
      if (!isSurface3DViewProperties(viewProperties)) {
        logSettingsControlDependenciesError(ViewIds.SURFACE_3D);
        return state;
      }
      const {
        viewSettings,
        axisFields,
        isEmptyView,
        isDrawable,
        perValueAxes,
        isListHeight,
      } = viewProperties;
      state.viewProperties = viewProperties;
      state.viewModifiers = getSurface3DViewModifiers({
        viewSettings,
        axisFields,
        isListHeight,
        perValueAxes,
        i18nLocale: state.i18nLocale,
      });
      state.rightMenu = [];
      /* `pngExporter`, not `getExportsForHtmlView`. The HTML path clones the DOM, and cloning a
         canvas does not carry its pixels - `recursiveTraversal` has no case for one, so the three
         chart layers rasterise as nothing and the export comes back blank but for the caption.
         Every other canvas-drawn view reads its own canvas instead, which is what the controller's
         `createExportCanvas` flattens the layers for. */
      /* `isDrawable` as well as `isEmptyView`. They are different questions - the first is the
         source-data check the view can make, the second is the gridding failure only the chart can
         see - and in that second state the layers still hold the last surface they painted, so
         `createExportCanvas` returns null and the user got a failure toast from an enabled menu. */
      state.exportToPng =
        isEmptyView || !isDrawable
          ? /* The noop is what disables the menu entry: `toSaveDropdownOptions` tests for exactly
             this identity. */
            asyncNoop
          : pngExporter({
              getExportContainer: () => getCurrentViewContext().getContainer(),
              getExportCanvas: () =>
                getCurrentViewContext().createExportCanvas?.() ?? null,
              viewId: ViewIds.SURFACE_3D,
              /* Hardcoded rather than read off the view properties, as for every other
                 viewpoint-only view here: the surface chart has no workspace-mode form, which
                 `Surface3DViewController` says of itself. The dual-mode views are the ones that
                 thread it through their own properties. */
              isViewpointMode: true,
              labels: getExportLabels(state.i18nLocale),
            });
      break;
    }
    case ViewIds.CONTOUR: {
      if (!isContourViewProperties(viewProperties)) {
        logSettingsControlDependenciesError(ViewIds.CONTOUR);
        return state;
      }
      const {
        viewSettings,
        axisFields,
        isEmptyView,
        isDrawable,
        isListHeight,
      } = viewProperties;
      state.viewProperties = viewProperties;
      state.viewModifiers = getContourViewModifiers({
        viewSettings,
        axisFields,
        isListHeight,
        i18nLocale: state.i18nLocale,
      });
      state.rightMenu = [];
      /* `pngExporter`, not `getExportsForHtmlView` - the comment this replaces had it backwards.
         The HTML path clones the DOM, and cloning a canvas does not carry its pixels, so the chart
         rasterised as nothing and the export came back blank but for the caption. The surface case
         above says the same thing and reaches for the same exporter. */
      /* `isDrawable` as well as `isEmptyView`, as above: the gridding can come back empty on source
         data this view reads as plottable, and there the chart mounts no canvas at all. */
      state.exportToPng =
        isEmptyView || !isDrawable
          ? /* The noop is what disables the menu entry: `toSaveDropdownOptions` tests for exactly
             this identity. */
            asyncNoop
          : pngExporter({
              getExportContainer: () => getCurrentViewContext().getContainer(),
              getExportCanvas: () =>
                getCurrentViewContext().createExportCanvas?.() ?? null,
              viewId: ViewIds.CONTOUR,
              isViewpointMode: true,
              labels: getExportLabels(state.i18nLocale),
            });
      break;
    }
    case ViewIds.SCATTER_3D:
    case ViewIds.BUBBLE_3D: {
      if (!isPoints3DViewProperties(viewProperties)) {
        logSettingsControlDependenciesError(mainViewId);
        return state;
      }
      const { viewSettings, numericFields, isEmptyView, showSizeVariation } =
        viewProperties;
      state.viewProperties = viewProperties;
      /* One case for both: the two differ in their id and in whether a size picker is offered,
         and the settings bar is otherwise identical. */
      state.viewModifiers = getPoints3DViewModifiers({
        viewId: mainViewId,
        viewSettings,
        numericFields,
        i18nLocale: state.i18nLocale,
        showSizeVariation,
      });
      state.rightMenu = [];
      /* `pngExporter` for the same reason as the contour and the surface: these draw on a canvas,
         and the DOM-cloning path carries none of its pixels. */
      state.exportToPng = isEmptyView
        ? asyncNoop
        : pngExporter({
            getExportContainer: () => getCurrentViewContext().getContainer(),
            getExportCanvas: () =>
              getCurrentViewContext().createExportCanvas?.() ?? null,
            viewId: mainViewId,
            isViewpointMode: true,
            labels: getExportLabels(state.i18nLocale),
          });
      break;
    }
    case ViewIds.NETWORK_3D: {
      if (!isNetwork3DViewProperties(viewProperties)) {
        logSettingsControlDependenciesError(mainViewId);
        return state;
      }
      const {
        viewSettings,
        numericFields,
        isEmptyView,
        isDrawable,
        isGrouped,
      } = viewProperties;
      state.viewProperties = viewProperties;
      state.viewModifiers = getNetwork3DViewModifiers({
        viewSettings,
        numericFields,
        isGrouped,
        i18nLocale: state.i18nLocale,
      });
      state.rightMenu = [];
      /* `pngExporter` for the same reason as the four charts: this draws on a canvas, and the
         DOM-cloning path carries none of its pixels. `isDrawable` as well as `isEmptyView`, as
         above - and here the second state is not transient: a browser with no WebGPU adapter
         draws a message instead of a canvas for as long as the view is open, so the entry stayed
         enabled and threw `export canvas not found` every time. */
      state.exportToPng =
        isEmptyView || !isDrawable
          ? asyncNoop
          : pngExporter({
              getExportContainer: () => getCurrentViewContext().getContainer(),
              getExportCanvas: () =>
                getCurrentViewContext().createExportCanvas?.() ?? null,
              viewId: ViewIds.NETWORK_3D,
              isViewpointMode: true,
              labels: getExportLabels(state.i18nLocale),
            });
      break;
    }
    case ViewIds.SPIDER_2: {
      if (!isSpiderViewProperties(viewProperties)) {
        logSettingsControlDependenciesError(ViewIds.SPIDER_2);
        return state;
      }
      const { viewSettings, numericFields, isEmptyView } = viewProperties;
      state.viewProperties = viewProperties;
      state.viewModifiers = getSpiderViewModifiers({
        viewSettings,
        numericFields,
        i18nLocale: state.i18nLocale,
      });
      state.rightMenu = [];
      state.exportToPng = isEmptyView
        ? asyncNoop
        : pngExporter({
            getExportContainer: () => getCurrentViewContext().getContainer(),
            getExportCanvas: () =>
              getCurrentViewContext().createExportCanvas?.() ?? null,
            viewId: ViewIds.SPIDER_2,
            isViewpointMode: true,
            labels: getExportLabels(state.i18nLocale),
          });
      break;
    }
    case ViewIds.POLAR: {
      if (!isPolarViewProperties(viewProperties)) {
        logSettingsControlDependenciesError(ViewIds.POLAR);
        return state;
      }
      const { viewSettings, numericFields, seriesFields, isEmptyView } =
        viewProperties;
      state.viewProperties = viewProperties;
      state.viewModifiers = getPolarViewModifiers({
        viewSettings,
        numericFields,
        seriesFields,
        i18nLocale: state.i18nLocale,
      });
      state.rightMenu = [];
      state.exportToPng = isEmptyView
        ? asyncNoop
        : pngExporter({
            getExportContainer: () => getCurrentViewContext().getContainer(),
            getExportCanvas: () =>
              getCurrentViewContext().createExportCanvas?.() ?? null,
            viewId: ViewIds.POLAR,
            isViewpointMode: true,
            labels: getExportLabels(state.i18nLocale),
          });
      break;
    }
    default: {
      state.viewModifiers = [];
      state.exportToPng = asyncNoop;
    }
  }

  return state;
};

const viewPropertiesUpdated = (
  state: ViewModifiersStreamShape,
  {
    viewProperties,
    viewId: viewPropertiesViewId,
  }: ExtractPayload<typeof updateViewProperties>
): ViewModifiersStreamShape =>
  resetViewModifiers({
    ...state,
    viewProperties,
    viewPropertiesViewId,
    viewContext: viewProperties.viewContext ?? state.viewContext,
  });

const mainViewChanged$ = activeView$.pipe(
  distinctUntilKeyChanged('mainViewId')
);

const viewChanged = (
  state: ViewModifiersStreamShape,
  { mainViewId }: ExtractStreamShape<typeof mainViewChanged$>
): ViewModifiersStreamShape => resetViewModifiers({ ...state, mainViewId });

const viewContextUpdated = (
  state: ViewModifiersStreamShape,
  viewContext: ViewContext
) => {
  // TODO: payload-level filtering is a workaround. The right way is to namespace
  // the action stream via dispatchAction's namespace parameter, like the external
  // settings bar does in MainPaneView. This requires a larger refactoring.
  // Both IDs must be present to reject; views that haven't opted into
  // viewInstanceId on either side keep working unchanged.
  if (
    viewContext.viewInstanceId &&
    state.viewProperties.viewInstanceId &&
    viewContext.viewInstanceId !== state.viewProperties.viewInstanceId
  ) {
    return state;
  }
  return resetViewModifiers({ ...state, viewContext });
};

const updateRelationshipViewViewSettings$ =
  viewpointModeSettingsForAllViews$.pipe(
    map(allViewpointModeViewSettings => {
      const { collapsedGroupIds, bundleRelationships } =
        allViewpointModeViewSettings.currentSettings[ViewIds.RELATIONSHIPS_3];

      return {
        collapsedGroupIds,
        bundleRelationships,
      };
    }),
    distinctUntilChanged((prev, next) => isEqual(prev, next)),
    debounceTime(DEFAULT_DEBOUNCE_TIME)
  );

const updateRelationshipViewViewSettings = (
  state: ViewModifiersStreamShape,
  updatedViewSettings: Partial<RelationshipsViewSettings>
) =>
  state.mainViewId === ViewIds.RELATIONSHIPS_3
    ? resetViewModifiers({
        ...state,
        viewProperties: {
          ...state.viewProperties,
          viewSettings: {
            ...state.viewProperties.viewSettings,
            ...updatedViewSettings,
          },
        },
      })
    : state;

const updateViewProperties$ = action$.pipe(
  ofType(updateViewProperties),
  extractPayload(),
  distinctUntilChanged(isSameViewProperties)
);

const i18nLocaleUpdated = (
  state: ViewModifiersStreamShape,
  i18nLocale: I18nLocale
): ViewModifiersStreamShape => resetViewModifiers({ ...state, i18nLocale });

const quickPerspectiveReferenceTypeFiltersUpdated = (
  state: ViewModifiersStreamShape,
  quickPerspectiveReferenceTypeFilters: Set<string> | null
): ViewModifiersStreamShape =>
  resetViewModifiers({ ...state, quickPerspectiveReferenceTypeFilters });

const loadedStatesUpdated = (
  state: ViewModifiersStreamShape,
  { loadedStates }: { loadedStates: LoadedState[] }
): ViewModifiersStreamShape => resetViewModifiers({ ...state, loadedStates });

const workspaceIdsUpdated = (
  state: ViewModifiersStreamShape,
  { workspaceIds }: { workspaceIds: ArdoqId[] }
): ViewModifiersStreamShape => resetViewModifiers({ ...state, workspaceIds });

const formattingFiltersUpdated = (
  state: ViewModifiersStreamShape,
  formattingFilters: (FormattingFilter | FilterInterfaceFilter)[]
): ViewModifiersStreamShape =>
  resetViewModifiers({ ...state, formattingFilters });

const reducers = [
  streamReducer(mainViewChanged$, viewChanged),
  streamReducer(updateViewProperties$, viewPropertiesUpdated),
  reducer(updateViewContext, viewContextUpdated),
  streamReducer(
    updateRelationshipViewViewSettings$,
    updateRelationshipViewViewSettings
  ),
  streamReducer(i18nLocale$, i18nLocaleUpdated),
  streamReducer(
    quickPerspectiveReferenceTypeFilters$,
    quickPerspectiveReferenceTypeFiltersUpdated
  ),
  streamReducer(loadedState$, loadedStatesUpdated),
  streamReducer(context$, workspaceIdsUpdated),
  streamReducer(formattingFilters$, formattingFiltersUpdated),
];

const defaultState: ViewModifiersStreamShape = {
  mainViewId: ViewIds.NONE,
  viewProperties: {},
  viewPropertiesViewId: ViewIds.NONE,
  viewModifiers: [],
  rightMenu: [],
  viewContext: DEFAULT_VIEW_CONTEXT,
  exportToPng: asyncNoop,
  exportToSvg: asyncNoop,
  addToDashboard: null,
  i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
  quickPerspectiveReferenceTypeFilters: null,
  loadedStates: [],
  workspaceIds: [],
  formattingFilters: [],
};

const viewModifiers$ = persistentReducedStream<ViewModifiersStreamShape>(
  'viewModifiers$',
  defaultState,
  reducers
);

export { viewModifiers$ };
